"use client";

import { Button } from "@/components/ui/button";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLegend,
	FieldSet,
} from "@/components/ui/field";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { createBlogPostsSubscription } from "@/lib/blog/static";
import { EmailSchema, cn } from "@/lib/utils";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { CircleCheckBigIcon, MailIcon } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as v from "valibot";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import ZeekMapsIcon from "../ui/icons/ZeekMapsIcon";

interface NewsletterFormProps {
	email?: string;
	className?: string;
}

const schema = v.object({
	email: EmailSchema,
});

export function NewsletterForm({ email, className }: NewsletterFormProps) {
	const [isSubscribed, setIsSubscribed] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const form = useForm({
		resolver: valibotResolver(schema),
		defaultValues: {
			email: email || "",
		},
	});

	async function onSubmit(data: v.InferInput<typeof schema>) {
		setIsLoading(true);
		try {
			const submittedEmail = data.email;
			const { data: subscriptionData, error: subscriptionError } =
				await createBlogPostsSubscription(submittedEmail, "newsletter-form");
			if (subscriptionError) throw subscriptionError;
			setIsSubscribed(true);
		} catch (error) {
			console.error(error);
			toast("Error signing up. Please try again", {
				icon: <MailIcon className="w-4 h-4" />,
				style: {
					background: "red",
					color: "white",
				},
			});
		} finally {
			setIsLoading(false);
		}
	}

	if (isSubscribed) {
		return <NewsletterFormSuccess />;
	}

	return (
		<div
			className={cn(
				"w-full flex flex-col items-center justify-center",
				className,
			)}
		>
			<form
				id="form-newsletter"
				onSubmit={form.handleSubmit(onSubmit)}
				className="w-full max-w-sm flex flex-col gap-4 items-center text-center justify-center"
			>
				<ZeekMapsIcon className="h-16 w-full self-center" />
				<FieldGroup>
					<FieldSet className="flex flex-col gap-4 items-center justify-center">
						<FieldLegend>
							<h2 className="text-3xl font-extrabold tracking-tighter text-primary">
								Subscribe to our newsletter
							</h2>
						</FieldLegend>
						<FieldDescription>
							Get the latest updates on new cities and features.
						</FieldDescription>
						<FieldGroup>
							{/* Email */}
							<Controller
								name="email"
								control={form.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid}>
										<InputGroup className="rounded-full">
											<InputGroupInput
												id="form-newsletter-email"
												type="text"
												autoComplete="email"
												placeholder="Enter your email"
												className="!placeholder-gray-400"
												aria-invalid={fieldState.invalid}
												{...field}
											/>
											<InputGroupAddon className="gap-2">
												<MailIcon />
											</InputGroupAddon>
										</InputGroup>
										<Button
											type="submit"
											className={cn(
												"w-full rounded-full border border-primary/20 px-2 py-1",
												"transition-all focus:ring-4 focus:ring-primary-200",
												"ring-offset-2 transition duration-200 hover:ring-2 hover:ring-green-500",
												isLoading && "cursor-not-allowed opacity-50",
											)}
										>
											{isLoading ? "Subscribing..." : "Subscribe"}
										</Button>

										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</Field>
								)}
							/>
						</FieldGroup>
					</FieldSet>
				</FieldGroup>
			</form>
		</div>
	);
}

export function NewsletterFormSuccess({ className }: { className?: string }) {
	return (
		<Card className={cn("bg-transparent border-none shadow-none", className)}>
			<CardHeader className="items-center justify-center text-center space-y-4">
				<CircleCheckBigIcon className="w-12 h-12 text-green-500" />
				<CardTitle className="text-3xl font-extrabold tracking-tighter">
					Thank you for subscribing to our newsletter!
				</CardTitle>
			</CardHeader>
			<CardContent>
				<p className="text-center text-sm text-primary/80">
					You will receive updates on new cities and features.
				</p>
			</CardContent>
		</Card>
	);
}
